import { McButton, McIcon, McLoadingIndicator, McTable } from '@maersk-global/mds-react-wrapper';
import React, { Fragment, FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ModalContext, ModalType } from '../../../common/modals/ModalProvider';
import { holidaysService } from '../../../common/service/holidays/holidays-service';
import { Holiday } from '../../../common/service/holidays/types';
import AffectedRenderer from '../affected-renderer';
import RemarksRenderer from '../remarks-renderer';
import { columns, mapString } from './consts';
import { getOriginalHolidayDate } from './helpers';
import HolidaysDrawer from './holidays-drawer';

type HolidaysProps = {
  selectedTerminal: string;
  gateMode?: boolean;
};

const Holidays: FunctionComponent<HolidaysProps> = ({ selectedTerminal, gateMode }) => {
  const { showModal } = useContext(ModalContext);
  const [holidays, setHolidays] = useState([] as Holiday[]);
  const [holidayToAdd, setHolidayToAdd] = useState<'current' | 'next' | undefined>(undefined);
  const [holidayToEdit, setHolidayToEdit] = useState<Holiday | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const { formatDate } = useIntl();

  const service = useMemo(() => holidaysService(), []);

  const fetch = async () => {
    setLoading(true);
    const resp = await service.fetchHolidays(selectedTerminal);
    setHolidays(resp || []);
    setLoading(false);
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- by design
  }, [selectedTerminal]);

  const handleDelete = async (id: number) => {
    await service.deleteHoliday(id);
    fetch();
  };

  const dataMapper = useCallback(
    (data: Holiday[]) =>
      data
        .sort((a, b) => (b.date > a.date || a.date === null ? -1 : 1))
        .filter((x) => (gateMode ? x.gate === 'NO' || x.gate === 'PARTIAL' : true))
        .map((h) => ({
          id: h.id,
          primaryHolidayId: h.primaryHolidayId,
          name: h.name,
          repeat: mapString(h.repeat),
          remarks: h.remarks,
          gate: mapString(h.gate),
          vesselOps: mapString(h.vesselOps),
          date: formatDate(h.date === null ? getOriginalHolidayDate(h.primaryHolidayId, holidays) : h.date, {
            weekday: 'long',
            month: 'long',
            day: '2-digit',
          }),
        })),
    [holidays, formatDate, gateMode],
  );

  const currentYearHolidays = useMemo(
    () => holidays.filter((h) => new Date(h.date).getFullYear() === new Date().getFullYear()),
    [holidays],
  );

  const currentYearHolidaysMapped = useMemo(() => dataMapper(currentYearHolidays), [currentYearHolidays, dataMapper]);

  const nextYearHolidays = useMemo(
    () => holidays.filter((h) => h.date === null || new Date(h.date).getFullYear() === new Date().getFullYear() + 1),
    [holidays],
  );

  const nextYearHolidaysMapped = useMemo(() => dataMapper(nextYearHolidays), [nextYearHolidays, dataMapper]);

  return (
    <div className="Holidays">
      {loading ? (
        <McLoadingIndicator label="Fetching data..." />
      ) : (
        <div className="Holidays__table-wrapper">
          <div className="Holidays__tilte-button">
            <h2 className="Holidays__title">National & public holidays {new Date().getFullYear()}</h2>
            {!gateMode ? (
              <McButton label="Add new holiday" icon="plus" click={() => setHolidayToAdd('current')} />
            ) : null}
          </div>
          {gateMode && !currentYearHolidaysMapped.length ? (
            <>
              <br />
              <div>N/A</div>
            </>
          ) : (
            <McTable columns={gateMode ? columns.slice(0, -1) : columns} datakey="id" data={currentYearHolidaysMapped}>
              {currentYearHolidays.map((row) => (
                <Fragment key={`${row.id}_actions`}>
                  <div slot={`${row.id}_gate`}>{<AffectedRenderer entity={row} type="gate" />}</div>
                  <div slot={`${row.id}_vesselOps`}>{<AffectedRenderer entity={row} type="vesselOps" />}</div>
                  <div slot={`${row.id}_remarks`}>{<RemarksRenderer remarks={row.remarks} />}</div>
                  <div slot={`${row.id}_actions`}>
                    <McButton
                      fit="small"
                      variant="plain"
                      appearance="primary"
                      icon="trash"
                      click={() => showModal(ModalType.DeleteHoliday, () => () => handleDelete(row.id))}
                    >
                      Delete
                    </McButton>
                    <McButton
                      fit="small"
                      variant="plain"
                      appearance="primary"
                      icon="pencil"
                      click={() => setHolidayToEdit(row)}
                    >
                      Edit
                    </McButton>
                  </div>
                </Fragment>
              ))}
            </McTable>
          )}
          {gateMode ? null : (
            <>
              <div className="Holidays__separator" />
              <div className="Holidays__tilte-button">
                <h2 className="Holidays__title">National & public holidays {new Date().getFullYear() + 1}</h2>
                <McButton label="Add new holiday" icon="plus" click={() => setHolidayToAdd('next')} />
              </div>
              <McTable columns={columns} datakey="id" data={nextYearHolidaysMapped}>
                {nextYearHolidays.map((row, index) => (
                  <Fragment key={row.id}>
                    <div slot={`${row.id}_date`}>
                      {row.date === null ? (
                        <div className="Holidays__date">
                          <McIcon icon="exclamation-triangle" color="#B80012" />
                          <span>{nextYearHolidaysMapped[index].date}</span>
                        </div>
                      ) : (
                        nextYearHolidaysMapped[index].date
                      )}
                    </div>
                    <div slot={`${row.id}_actions`}>
                      {row.isPrimary ? (
                        <McButton
                          fit="small"
                          variant="plain"
                          appearance="primary"
                          icon="trash"
                          click={() => showModal(ModalType.DeleteHoliday, () => () => handleDelete(row.id))}
                        >
                          Delete
                        </McButton>
                      ) : null}
                      <McButton
                        fit="small"
                        variant="plain"
                        appearance="primary"
                        icon="pencil"
                        click={() =>
                          setHolidayToEdit({
                            ...row,
                            tempDate:
                              row.date === null
                                ? getOriginalHolidayDate(row.primaryHolidayId, currentYearHolidays)
                                : undefined,
                          })
                        }
                      >
                        Edit
                      </McButton>
                    </div>
                    <div slot={`${row.id}_remarks`}>{<RemarksRenderer remarks={row.remarks} />}</div>
                  </Fragment>
                ))}
              </McTable>
            </>
          )}
        </div>
      )}

      {(holidayToAdd || holidayToEdit) && (
        <HolidaysDrawer
          open={true}
          selectedTerminal={selectedTerminal}
          defaultValues={holidayToEdit}
          action={holidayToEdit ? 'edit' : holidayToAdd === 'current' ? 'add' : 'add-next'}
          onSuccess={fetch}
          onClose={() => {
            setHolidayToEdit(undefined);
            setHolidayToAdd(undefined);
          }}
        />
      )}
    </div>
  );
};

export default Holidays;
